import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  IconButton,
  CircularProgress,
  Tooltip,
  TextField,
  Input,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardMedia,
  CardActions,
} from "@mui/material";
import {
  Draw as DrawIcon,
  TextFields as TextIcon,
  Upload as UploadIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import axios from "axios";
import Layout from "../components/layout/Esign";
import config from "../config.js";
import { useSelector } from "react-redux";
import DeleteIcon from '@mui/icons-material/Delete';

const SignatureComponent = () => {
  const { token } = useSelector((state) => state.auth);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signMethod, setSignMethod] = useState("draw");
  const [loading, setLoading] = useState(false);
  const [typedSignature, setTypedSignature] = useState("");
  const [selectedFont, setSelectedFont] = useState("cursive");
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const { API_BASE_URL } = config;
  const [signatures, setSignatures] = useState([]);

  const fonts = [
    { value: "cursive", label: "Cursive" },
    { value: '"Times New Roman", serif', label: "Times New Roman" },
    { value: '"Brush Script MT", cursive', label: "Brush Script" },
    { value: '"Dancing Script", cursive', label: "Dancing Script" },
    { value: '"Great Vibes", cursive', label: "Great Vibes" },
    { value: '"Arial", sans-serif', label: "Arial" },
  ];

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();

    
    canvas.width = rect.width;
    canvas.height = rect.height;

    
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = "#000000";
    context.lineWidth = 2;

    contextRef.current = context;
  };

  useEffect(() => {
    initializeCanvas();
    window.addEventListener("resize", initializeCanvas);
    return () => window.removeEventListener("resize", initializeCanvas);
  }, []);

  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    if (event.touches && event.touches[0]) {
      return {
        x: event.touches[0].clientX - rect.left,
        y: event.touches[0].clientY - rect.top,
      };
    }
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const startDrawing = (event) => {
    event.preventDefault();
    const coords = getCoordinates(event);

    contextRef.current.beginPath();
    contextRef.current.moveTo(coords.x, coords.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (event) => {
    if (!isDrawing) return;
    event.preventDefault();

    const coords = getCoordinates(event);
    contextRef.current.lineTo(coords.x, coords.y);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (signMethod === "draw") {
      const canvas = canvasRef.current;
      contextRef.current.clearRect(0, 0, canvas.width, canvas.height);
    } else if (signMethod === "type") {
      setTypedSignature("");
    } else if (signMethod === "upload") {
      setUploadedSignature(null);
    }
    setHasSignature(false);
  };

  const handleTypeSignature = (event) => {
    setTypedSignature(event.target.value);
    setHasSignature(!!event.target.value);
  };

  const handleFontChange = (event) => {
    setSelectedFont(event.target.value);
  };

  const handleUploadSignature = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedSignature(e.target.result);
        setHasSignature(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveSignature = async () => {
    if (!hasSignature) return;
    setLoading(true);

    try {
      let signatureData;
      if (signMethod === "draw") {
        signatureData = canvasRef.current.toDataURL("image/png");
      } else if (signMethod === "type") {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = 600;
        canvas.height = 200;
        context.font = `48px ${selectedFont}`;
        context.fillStyle = "#000000";
        context.fillText(typedSignature, 10, 100);
        signatureData = canvas.toDataURL("image/png");
      } else if (signMethod === "upload") {
        signatureData = uploadedSignature;
      }

      await axios.post(
        `${API_BASE_URL}/api/v1/saveSignatures`,
        {
          signatureType: signMethod,
          signature: signatureData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert("Signature saved successfully!");
      fetchSignatures(); 
    } catch (error) {
      console.error("Error saving signature:", error);
      alert("Failed to save signature. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderSignatureInput = () => {
    switch (signMethod) {
      case "draw":
        return (
          <canvas
            ref={canvasRef}
            style={{
              width: "100%",
              height: "200px",
              touchAction: "none",
              cursor: "crosshair",
              backgroundColor: "#ffffff",
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        );
      case "type":
        return (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Font Style</InputLabel>
              <Select
                value={selectedFont}
                onChange={handleFontChange}
                label="Font Style"
              >
                {fonts.map((font) => (
                  <MenuItem
                    key={font.value}
                    value={font.value}
                    sx={{ fontFamily: font.value }}
                  >
                    {font.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type your signature"
              value={typedSignature}
              onChange={handleTypeSignature}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: selectedFont,
                  fontSize: "2rem",
                  height: "100px",
                  lineHeight: "100px",
                },
              }}
            />
          </Stack>
        );
      case "upload":
        return (
          <Box
            sx={{
              height: "200px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {uploadedSignature ? (
              <img
                src={uploadedSignature}
                alt="Uploaded signature"
                style={{ maxHeight: "180px", maxWidth: "100%" }}
              />
            ) : (
              <Box sx={{ textAlign: "center" }}>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadSignature}
                  sx={{ display: "none" }}
                  id="signature-upload"
                />
                <label htmlFor="signature-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                  >
                    Upload Signature Image
                  </Button>
                </label>
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };
  
  const fetchSignatures = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getUserSignatures`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
     
      if (response.data && response.data.data && response.data.data.signatures) {
        setSignatures(response.data.data.signatures);
        
      } else {
        setSignatures([]);
        console.warn("No signatures found in API response", response.data);
      }
    } catch (error) {
      console.error("Error fetching signatures:", error);
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  };
  
  const deleteSignature = async (signatureId) => {
    try {
      setLoading(true);
      const response = await axios.delete(
        `${API_BASE_URL}/api/v1/deleteSignature/${signatureId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (response.data.success) {
        setSignatures(prevSignatures => 
          prevSignatures.filter(sig => sig.id !== signatureId)
        );
      } else {
        throw new Error(response.data.message || 'Failed to delete signature');
      }
    } catch (error) {
      console.error("Error deleting signature:", error);
      alert(error.response?.data?.message || "Failed to delete signature");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignatures();
  }, []);

  return (
    <Layout>
    <Box sx={{ p: 2 }}>
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          My Signatures
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : signatures && signatures.length > 0 ? (
          <Grid 
          container 
          spacing={2} 
          sx={{
            width: '100%',
            margin: '0 auto',
            padding: '16px'
          }}
        >
          {signatures.map((signature) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              key={signature.id}
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Card sx={{ 
                width: '100%',
                maxWidth: 350,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ 
                  height: 200,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 2,
                  backgroundColor: '#f5f5f5'
                }}>
                  <CardMedia
                    component="img"
                    sx={{
                      objectFit: 'contain',
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }}
                    image={signature.url}
                    alt={`${signature.type} signature`}
                  />
                </Box>
                <CardActions sx={{ 
                  justifyContent: 'space-between',
                  padding: 2
                }}>
                  <Typography variant="subtitle2">
                    {signature.type} Signature
                  </Typography>
                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => deleteSignature(signature.id)}
                    size="small"
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        ) : (
          <Typography>No signatures found</Typography>
        )}
      </Paper>

      
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Typography variant="h6">Signature Method</Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant={signMethod === "draw" ? "contained" : "outlined"}
              onClick={() => {
                setSignMethod("draw");
                setHasSignature(false);
                clearSignature();
              }}
              startIcon={<DrawIcon />}
            >
              Draw
            </Button>
            <Button
              variant={signMethod === "type" ? "contained" : "outlined"}
              onClick={() => {
                setSignMethod("type");
                setHasSignature(false);
                clearSignature();
              }}
              startIcon={<TextIcon />}
            >
              Type
            </Button>
            <Button
              variant={signMethod === "upload" ? "contained" : "outlined"}
              onClick={() => {
                setSignMethod("upload");
                setHasSignature(false);
                clearSignature();
              }}
              startIcon={<UploadIcon />}
            >
              Upload
            </Button>
          </Box>

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              overflow: "hidden",
              position: "relative",
              minHeight: "200px",
              backgroundColor: "#f5f5f5",
              p: 2,
              "& canvas": {
                maxWidth: "100%",
                objectFit: "contain"
              }
            }}
          >
            {renderSignatureInput()}
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255, 255, 255, 0.7)",
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Tooltip title="Clear signature">
              <span>
                <IconButton
                  onClick={clearSignature}
                  disabled={!hasSignature}
                  edge="end"
                  aria-label="Clear signature"
                  sx={{ color: "#EB5A3C" }}
                >
                  <ClearIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Save signature">
              <span>
                <IconButton
                  color="primary"
                  onClick={saveSignature}
                  disabled={loading || !hasSignature}
                  edge="end"
                  aria-label="Save signature"
                  sx={{ color: "#EB5A3C" }}
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </Paper>
    </Box>
  </Layout>
  );
};

export default SignatureComponent;
